/**
 * PipelineStrip — the admin project detail's four-stage annotator pipeline:
 * Invited → Demo → Phase 1 → Phase 2, one count per stage. Phase 2 is
 * terminal; the factory never creates phase 3.
 *
 * Boundaries are **manual approval gates** up to and including the
 * `autoAdvanceFromPhase` prop (mirrors each project's
 * `gate_policy.auto_advance_from_phase`, src/types/factory.ts): entering a
 * phase numbered `P` (demo=0, phase1=1, phase2=2) requires a
 * gate iff `P <= autoAdvanceFromPhase` — that final gated boundary is the
 * last human checkpoint before any ungated transition. Phase 2 itself is
 * terminal and never auto-advances. Defaults to `2` so a
 * caller that doesn't thread the real value renders the historical fixed
 * shape (demo→P1, P1→P2 gated) unchanged.
 *
 * Hatched background = "auto-advances, no approval needed" for a phase
 * numbered `P >= autoAdvanceFromPhase`. Radius stays flat (`rounded` /
 * `rounded-sharp` only) even though the mockup's `.gate` marker is a circle
 * — the flat-style rule overrides that one visual detail (never
 * `rounded-full`).
 */

import React from "react";

export interface PipelineCounts {
  // `invited` (eligible, not-yet-accepted annotators) has no backing
  // endpoint today — Project carries no invite list and Enrollment only
  // exists once an annotator has accepted (see AdminProjectDetail.tsx).
  // `null` here means "no data source", rendered as "—" — never fabricate
  // a 0 for a count we structurally can't compute (no-silent-fallbacks).
  invited: number | null;
  demo: number;
  phase1: number;
  phase2: number;
}

export type PipelineStageKey = keyof PipelineCounts;

const STAGE_ORDER: PipelineStageKey[] = ["invited", "demo", "phase1", "phase2"];

const STAGE_LABEL: Record<PipelineStageKey, string> = {
  invited: "Invited",
  demo: "Demo",
  phase1: "Phase 1",
  phase2: "Phase 2",
};

// Phase number each stage represents for gate/auto-advance derivation.
// `invited` and `demo` are deliberately absent — the invited→demo boundary
// is never a gate (accepting into demo is self-serve, not an approval
// step) and neither stage can ever be an auto-advance stage.
const STAGE_PHASE_NUMBER: Partial<Record<PipelineStageKey, number>> = {
  phase1: 1,
  phase2: 2,
};

// Human-readable boundary label for the ProjectConfigPanel's "Gates" copy,
// keyed by the PRECEDING stage (same key shape as the gate-after Set below).
const STAGE_BOUNDARY_LABEL: Partial<Record<PipelineStageKey, string>> = {
  demo: "demo→P1",
  phase1: "P1→P2",
};

/** Boundaries (keyed by the preceding stage) that carry a manual-approval
 *  gate for a given `autoAdvanceFromPhase` floor: the boundary into stage
 *  `next` is gated iff `next`'s phase number is <= the floor. */
function computeGateAfterStages(autoAdvanceFromPhase: number): Set<PipelineStageKey> {
  const gated = new Set<PipelineStageKey>();
  for (let i = 0; i < STAGE_ORDER.length - 1; i++) {
    const preceding = STAGE_ORDER[i];
    const next = STAGE_ORDER[i + 1];
    const nextPhaseNumber = STAGE_PHASE_NUMBER[next];
    if (nextPhaseNumber !== undefined && nextPhaseNumber <= autoAdvanceFromPhase) {
      gated.add(preceding);
    }
  }
  return gated;
}

/** Non-terminal stages that advance without approval for a given floor. */
function computeAutoAdvanceStages(autoAdvanceFromPhase: number): Set<PipelineStageKey> {
  const auto = new Set<PipelineStageKey>();
  for (const stage of STAGE_ORDER) {
    const phaseNumber = STAGE_PHASE_NUMBER[stage];
    if (phaseNumber !== undefined && phaseNumber >= autoAdvanceFromPhase && phaseNumber < 2) {
      auto.add(stage);
    }
  }
  return auto;
}

/** Ordered list of gated-boundary labels (e.g. `["demo→P1", "P1→P2"]`) for
 *  a given floor — used by ProjectConfigPanel's "Gates" copy so the two
 *  places that describe gate placement can't drift out of sync again. */
export function gatedBoundaryLabels(autoAdvanceFromPhase: number): string[] {
  const gated = computeGateAfterStages(autoAdvanceFromPhase);
  return STAGE_ORDER.filter((stage) => gated.has(stage)).map(
    (stage) => STAGE_BOUNDARY_LABEL[stage]!,
  );
}

function GateMarker() {
  return (
    <span
      data-testid="pipeline-gate"
      aria-label="manual approval gate"
      title="Manual approval gate"
      className="relative z-10 -mx-[9px] flex h-[18px] w-[18px] shrink-0 items-center justify-center self-center rounded-sharp border border-warn/40 bg-bg text-warn"
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.4}
        className="h-2.5 w-2.5"
        aria-hidden="true"
      >
        <rect x="5" y="11" width="14" height="9" rx="1" />
        <path d="M8 11V8a4 4 0 0 1 8 0v3" />
      </svg>
    </span>
  );
}

interface StageProps {
  stageKey: PipelineStageKey;
  count: number | null;
  meta?: string;
  current: boolean;
  auto: boolean;
}

function Stage({ stageKey, count, meta, current, auto }: StageProps) {
  return (
    <div
      data-testid={`pipeline-stage-${stageKey}`}
      data-current={current}
      className={[
        "flex min-w-[104px] flex-1 flex-col gap-1.5 border border-border py-3 pl-4 pr-3",
        current ? "bg-primary/15 border-signal-stroke/40" : "bg-surface",
      ].join(" ")}
      style={
        auto
          ? {
              backgroundImage:
                "repeating-linear-gradient(135deg, transparent, transparent 7px, rgba(255,255,255,0.03) 7px, rgba(255,255,255,0.03) 14px)",
            }
          : undefined
      }
    >
      <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-fg-dim">
        {STAGE_LABEL[stageKey]}
      </span>
      <span
        className={`font-mono text-[22px] leading-none tracking-tight ${current ? "text-signal-text-tint" : "text-fg"}`}
      >
        {count === null ? "—" : count}
      </span>
      {meta && <span className="text-[10.5px] text-fg-faint">{meta}</span>}
      {auto && (
        <span data-testid="pipeline-stage-auto" className="sr-only">
          auto-advance
        </span>
      )}
    </div>
  );
}

export interface PipelineStripProps {
  counts: PipelineCounts;
  /** Optional per-stage caption, e.g. "20 · 15+5 GT" (mockup `.pmeta`). */
  meta?: Partial<Record<PipelineStageKey, string>>;
  /** Which stage to highlight as the current focus (mockup `.pstage.cur`). */
  currentStage?: PipelineStageKey;
  /** The project's `gate_policy.auto_advance_from_phase`. Defaults to `2`,
   *  the historical fixed shape (demo→P1, P1→P2 gated), for callers that
   *  haven't threaded the real per-project value. */
  autoAdvanceFromPhase?: number;
}

export function PipelineStrip({
  counts,
  meta,
  currentStage,
  autoAdvanceFromPhase = 2,
}: PipelineStripProps) {
  const gateAfterStages = computeGateAfterStages(autoAdvanceFromPhase);
  const autoAdvanceStages = computeAutoAdvanceStages(autoAdvanceFromPhase);
  return (
    <div>
      <div className="flex w-full items-stretch overflow-x-auto">
        {STAGE_ORDER.map((stageKey, i) => (
          <React.Fragment key={stageKey}>
            <Stage
              stageKey={stageKey}
              count={counts[stageKey]}
              meta={meta?.[stageKey]}
              current={currentStage === stageKey}
              auto={autoAdvanceStages.has(stageKey)}
            />
            {i < STAGE_ORDER.length - 1 && gateAfterStages.has(stageKey) && <GateMarker />}
          </React.Fragment>
        ))}
      </div>
      <div className="mt-2.5 flex flex-wrap gap-4 text-[11px] text-fg-dim">
        <span className="inline-flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className="inline-flex h-[11px] w-[11px] items-center justify-center rounded-sharp border border-warn/40 text-warn"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.4}
              className="h-1.5 w-1.5"
            >
              <rect x="5" y="11" width="14" height="9" rx="1" />
              <path d="M8 11V8a4 4 0 0 1 8 0v3" />
            </svg>
          </span>
          manual approval gate
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className="inline-block h-[11px] w-[11px] rounded-sharp bg-primary"
          />
          current focus
        </span>
        <span>hatched = auto-advances, no approval</span>
      </div>
    </div>
  );
}

export default PipelineStrip;
